import React, { useEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useStreamData } from '@lib/hooks/useStreamData';
import { AnimatedTile } from './talkingTiles/helper';
import ResultTile from './talkingTiles/ResultTile';
import {
  useSaveTilesState,
  useRestoreTilesState,
  useTalkingTilesSetup,
  useEngagementHandling,
  useTileManager,
  useFirestoreSubscription,
  useRemoveOldTilesState
} from './talkingTiles/hooks';

import useInteractionSound from '@components/Presentation/hooks/useInteractionSound';

const TalkingTiles = forwardRef(({
  streamId, currentRosResult, currentRosFeature, platformType,
  startInteraction, setCurrentFeatureId, interactionType,
  setShowRosModel, moderationModeRef, updateFSMeta,
  setShowNotification, setStartTime, layoutMode, isSandbox,
  pinnedCommentRef,
  setTotalResponders,
  isMuted
}, ref) => {
  const featureSettings = useMemo(() => ({
    ...currentRosFeature,
    type: currentRosFeature?.type || 'talkingTiles',
    setting: {
      ...currentRosFeature.setting,
      layoutMode,
      isSandbox
    }
  }), [currentRosFeature, layoutMode, isSandbox]);

  const { playSound } = useInteractionSound({
    featureSettings,
    soundKey: 'talkingTilesSoundOptions',
    isEnabledKey: 'isTalkingTilesSoundEnabled',
    isMuted
  });

  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setFeatureId: s.setFeatureId
  }));

  const { isRos } = interactionType;
  const containerRef = useRef(null);
  const [tiles, setTiles] = useState([]);
  const isSmallerLayout = useMemo(() => featureSettings?.setting?.layoutMode === 'square' || featureSettings?.setting?.isSandbox, [featureSettings]);
  const baseContentPx = isSmallerLayout ? 24 : 28;
  const [fontPx, setFontPx] = useState(baseContentPx);
  const fontScale = useMemo(() => Math.max(0.25, fontPx / baseContentPx), [fontPx, baseContentPx]);

  const {
    ttId,
    setttId,
    resultData,
    comments,
    setupTalkingTiles
  } = useTalkingTilesSetup(currentRosFeature?.id, currentRosResult, setCurrentFeatureId, setFeatureId, setTotalResponders);

  const {
    managerRef,
    processedCommentsCount,
    initializeManager,
    processedCommentIds,
    hasRestoredRef,
  } = useTileManager(containerRef, featureSettings, startInteraction, setTiles, ttId, playSound);

  const { saveTilesLocalStorage } = useSaveTilesState(managerRef, ttId, startInteraction);

  useEffect(() => {
    useRemoveOldTilesState();
    return () => {
      saveTilesLocalStorage();
    };
  }, [saveTilesLocalStorage]);

  const {
    isResultTileLoading,
    handleTalkingTiles,
    unsubscribeFromFirestore,
    stopTalkingTiles
  } = useEngagementHandling(
    streamId,
    platformType,
    currentRosFeature,
    setupTalkingTiles,
    updateFSMeta,
    firestoreUnsub,
    ttId,
    saveTilesLocalStorage,
    resultData,
  );

  useFirestoreSubscription(
    ttId,
    isRos,
    streamId,
    setFirestoreUnsub,
    managerRef,
    processedCommentsCount,
    setShowNotification,
    setTotalResponders,
    comments,
    startInteraction,
    processedCommentIds,
    hasRestoredRef
  );

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleTalkingTiles({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handleTalkingTiles({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const manageTalkingTilesInteraction = () => {
    if (startInteraction) {
      handleStartInteraction();
      pinnedCommentRef?.current?.close();
    } else if (ttId) {
      stopTalkingTiles();
      pinnedCommentRef?.current?.close();
    } else if (currentRosResult && currentRosResult.length > 0) {
      handleTalkingTiles({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    }
  };

  useEffect(() => {
    if (!(ttId && isRos)) return;
    const controller = new AbortController();
    const signal = controller.signal;

    window.addEventListener('beforeunload', () => {
      saveTilesLocalStorage();
    }, { signal });

    return () => {
      controller.abort();
    };
  }, [ttId, startInteraction]);

  useEffect(() => {
    if (!currentRosFeature.id) return;
    if (!startInteraction) return;

    setShowRosModel(false);
    manageTalkingTilesInteraction();
  }, [currentRosFeature.id, startInteraction]);

  const { restoreTilesState } = useRestoreTilesState(managerRef, ttId, startInteraction, currentRosResult, processedCommentIds, hasRestoredRef, processedCommentsCount);

  useEffect(() => {
    if (!containerRef.current) return;

    if (!managerRef.current && startInteraction) {
      initializeManager();
    }

    resultData.current = currentRosResult[0]?.comments || [];

    return () => {
      if (managerRef.current) {
        processedCommentsCount.current = 0;
      }
    };
  }, [currentRosFeature.id, containerRef.current, startInteraction, initializeManager]);

  useEffect(() => {
    if (managerRef.current && startInteraction) {
      let raf = requestAnimationFrame(() => {
        managerRef.current && managerRef.current.setFontScale(fontScale);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [fontScale, managerRef.current, startInteraction]);

  useEffect(() => {
    setFontPx(baseContentPx);
  }, [baseContentPx]);

  useEffect(() => {
    if (!managerRef.current || !ttId) return;

    managerRef.current.setCurrentRosFeature(featureSettings);
    managerRef.current.setTtId(ttId);
    managerRef.current.updateExistingTiles();
  }, [currentRosFeature, ttId, managerRef.current]);

  const className = `h-full scrollbar-hide ${!startInteraction ? 'overflow-y-scroll ' : 'overflow-hidden  '}`;
  const showUsername = featureSettings?.setting?.showUserName;

  useEffect(() => {
    if (pinnedCommentRef?.current?.isOpen()) {
      const comment = pinnedCommentRef.current.getComment();
      pinnedCommentRef.current.showPinnedComment({ ...comment, showUsername });
    }
    return () => {
      pinnedCommentRef?.current?.close();
    };
  }, [showUsername]);

  useImperativeHandle(ref, () => ({
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleTalkingTiles({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  useEffect(() => {
    if (featureSettings?.setting?.talkingTilesFontSize) {
      setFontPx(prev => baseContentPx * featureSettings?.setting?.talkingTilesFontSize);
    }
  }, [featureSettings?.setting?.talkingTilesFontSize]);

  return (
    <div
      className={className}
      style={{
        width: '100%',
        position: 'relative',
      }}
    >
      {!startInteraction ? (
        <ResultTile isResultTileLoading={isResultTileLoading} tiles={resultData.current} currentRosFeature={currentRosFeature} fontScale={fontScale} />
      ) : (
        <div
          className="flex flex-col items-center justify-center h-full w-full overflow-hidden relative  "
          ref={containerRef}
          style={{ fontSize: `${Math.round(16 * fontScale)}px` }}
        >

          {tiles.map(tile => (
            <AnimatedTile
              onPress={() => {
                pinnedCommentRef?.current?.showPinnedComment({ username: tile.username, content: tile.words, showUsername });
              }}
              key={tile.id}
              tile={tile}
              tileGap={managerRef.current?.tileGap || 10}
              currentRosFeature={featureSettings}
            />
          ))}
        </div>
      )}

    </div>
  );
});

export default TalkingTiles;
